/* eslint-disable lingui/no-unlocalized-strings */
import { Trans } from '@lingui/react/macro';
import * as Sentry from '@sentry/react';

import Paragraph from '@/design_system/Typography/Paragraph';

export function updateSentryContext({
  user,
  organization,
}: {
  user?: {
    id: string;
    email: string;
  } | null;
  organization?: {
    id: string;
    name: string;
    slug: string;
  } | null;
}) {
  const currentSentryUser = user ?? null;
  const currentSentryOrganization = organization ?? null;

  if (!import.meta.env.VITE_SENTRY_DSN) {
    return;
  }

  Sentry.setUser(currentSentryUser ?? null);

  Sentry.setTag('organization', currentSentryOrganization?.slug ?? '');

  const scope = Sentry.getCurrentScope();

  scope.setExtra('user', currentSentryUser);
  scope.setExtra('organization', currentSentryOrganization);
}

export function initSentry() {
  try {
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: window.location.host,
        dataCollection: {},
        integrations: [
          Sentry.replayIntegration({
            maskAllText: false,
            maskAllInputs: true,
            blockAllMedia: true,
          }),
          Sentry.captureConsoleIntegration({ levels: ['error'] }),
          Sentry.eventFiltersIntegration(),
        ],
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        // Sentry SDK normalize any contextual data to a given depth. Any data beyond this depth
        // will be trimmed and marked using its type instead ([Object] or [Array]),
        // without walking the tree any further. By default, walking is performed 3 levels deep.
        // Arbitrary set it to 10 to ensure enough (most of the time all) data is captured.
        normalizeDepth: 10,
        ignoreErrors: [
          'TypeError: Failed to fetch', // Network errors
          'TypeError: Load failed', // Network errors
          'TypeError: NetworkError when attempting to fetch resource', // Network errors
          'Error uploading', // From FileUpload components
          'Importing a module script failed', // Vite preload errors, already handled by reloadOnPreloadError
        ],
        attachStacktrace: false, // We don't need the stack trace
      });
    }
  } catch (err) {
    console.error(err);
  }
}

export function captureException(error: Error) {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureException(error);
  } else {
    console.error(error);
  }
}

export function captureMessage(
  message: string,
  captureContext?: Parameters<typeof Sentry.captureMessage>[1]
) {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureMessage(message, captureContext);
  } else {
    console.error(message);
  }
}

export const ErrorBoundary = (props: Sentry.ErrorBoundaryProps) => {
  return (
    <Sentry.ErrorBoundary
      fallback={
        <Paragraph>
          <Trans id="errorBoundary.fallback">
            An unknown error occurred. Please {/* eslint-disable-next-line */}
            <a
              onClick={() => window.location.reload()}
              style={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              refresh the page
            </a>
          </Trans>
        </Paragraph>
      }
      {...props}
    />
  );
};
